(() => {
  "use strict";

  const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const all = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const visibleFocusableChildren = (container) =>
    all(FOCUSABLE, container).filter((element) => {
      if (element.closest("[hidden]")) return false;
      return element.getClientRects().length > 0;
    });

  const initializeHeader = (root) => {
    if (!root || root.dataset.ppShellReady === "true") return;
    root.dataset.ppShellReady = "true";

    const controller = new AbortController();
    const { signal } = controller;
    const header = root.querySelector("[data-glass-header]");
    const megaTrigger = root.querySelector("[data-nav-menu-toggle]");
    const megaMenu = root.querySelector("[data-pp-mega-menu]");
    const mobileToggle = root.querySelector("[data-mobile-nav-toggle]");
    const mobileDrawer = root.querySelector("[data-mobile-nav]");
    const mobileBackdrop = root.querySelector("[data-mobile-nav-backdrop]");
    const cartLink = root.querySelector("#cart-icon-bubble");
    const desktopNavigation = window.matchMedia("(min-width: 1101px)");
    const openNavigationLabel = mobileToggle?.dataset.navOpenLabel || "Open menu";
    const closeNavigationLabel = mobileToggle?.dataset.navCloseLabel || "Close menu";
    const cartLabel = root.dataset.cartLabel || "View bag";
    let lockedScrollY = null;
    let lockedBodyStyles = null;
    let scrollFrame = 0;

    const isExpanded = (trigger) => trigger?.getAttribute("aria-expanded") === "true";

    const updateRootState = () => {
      root.classList.toggle("nav-is-open", isExpanded(megaTrigger) || isExpanded(mobileToggle));
    };

    const setExpanded = (trigger, panel, expanded) => {
      if (!trigger || !panel) return;
      trigger.setAttribute("aria-expanded", String(expanded));
      trigger.classList.toggle("is-open", expanded);
      panel.classList.toggle("is-open", expanded);
      panel.hidden = !expanded;
      updateRootState();
    };

    const synchronizeCart = () => {
      if (!cartLink) return;
      const countBubble = cartLink.querySelector(".cart-count-bubble");
      const countText = countBubble?.dataset.cartCount || countBubble?.querySelector("[aria-hidden='true']")?.textContent || "0";
      const count = Math.max(0, Number.parseInt(countText, 10) || 0);

      if (countBubble) {
        countBubble.classList.add("glass-cart-count");
        countBubble.dataset.cartCount = String(count);
        countBubble.hidden = count === 0;
      }

      cartLink.setAttribute(
        "aria-label",
        count > 0 ? `${cartLabel}, ${count} ${count === 1 ? "item" : "items"}` : cartLabel,
      );
    };

    const setPageInert = (inert) => {
      const pageRegions = [
        ...all("[data-pp-announcement]"),
        ...all("#MainContent"),
        ...all(".shopify-section-group-footer-group"),
        ...all(".site-footer"),
        ...all(".glass-primary-nav", root),
        ...all(".glass-header-rail .brand", root),
        ...all(".glass-header-rail .header-actions", root),
      ];
      pageRegions.forEach((region) => {
        region.inert = inert;
      });
    };

    const lockPageScroll = (keepHeaderPinned) => {
      if (lockedScrollY !== null) return;
      lockedScrollY = window.scrollY;
      lockedBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        right: document.body.style.right,
        left: document.body.style.left,
        width: document.body.style.width,
      };
      document.documentElement.classList.toggle("nav-open-from-scroll", keepHeaderPinned);
      document.body.style.position = "fixed";
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.right = "0";
      document.body.style.left = "0";
      document.body.style.width = "100%";
    };

    const unlockPageScroll = () => {
      if (lockedScrollY === null || !lockedBodyStyles) return;
      const scrollY = lockedScrollY;
      Object.assign(document.body.style, lockedBodyStyles);
      lockedScrollY = null;
      lockedBodyStyles = null;
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      window.scrollTo(0, scrollY);
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
      document.documentElement.classList.remove("nav-open-from-scroll");
    };

    const positionMobileDrawer = () => {
      if (!mobileDrawer || !mobileToggle) return false;
      const rail = root.querySelector(".glass-header-rail");
      if (!rail) return false;
      const railRect = rail.getBoundingClientRect();
      mobileDrawer.style.setProperty("--glass-mobile-menu-top", `${Math.ceil(railRect.bottom + 8)}px`);
      return railRect.top <= 10.5;
    };

    const closeMegaMenu = (restoreFocus = false) => {
      if (!megaTrigger || !megaMenu) return;
      setExpanded(megaTrigger, megaMenu, false);
      if (restoreFocus && document.contains(megaTrigger)) {
        megaTrigger.focus({ preventScroll: true });
      }
    };

    const openMegaMenu = () => {
      if (!megaTrigger || !megaMenu) return;
      if (isExpanded(mobileToggle)) closeMobileDrawer(false);
      setExpanded(megaTrigger, megaMenu, true);
      if (megaTrigger.dataset.focusMenu === "true") {
        window.setTimeout(() => {
          if (!isExpanded(megaTrigger) || megaMenu.hidden) return;
          const firstFocusable = visibleFocusableChildren(megaMenu)[0];
          firstFocusable?.focus({ preventScroll: true });
        }, 80);
      }
    };

    const closeMobileDrawer = (restoreFocus = false) => {
      if (!mobileToggle || !mobileDrawer) return;
      setExpanded(mobileToggle, mobileDrawer, false);
      mobileToggle.setAttribute("aria-label", openNavigationLabel);
      document.documentElement.classList.remove("nav-is-open");
      document.body.classList.remove("nav-is-open");
      setPageInert(false);
      unlockPageScroll();
      if (restoreFocus && document.contains(mobileToggle)) {
        mobileToggle.focus({ preventScroll: true });
      }
    };

    const openMobileDrawer = () => {
      if (!mobileToggle || !mobileDrawer) return;
      closeMegaMenu();
      const keepHeaderPinned = positionMobileDrawer();
      setExpanded(mobileToggle, mobileDrawer, true);
      mobileToggle.setAttribute("aria-label", closeNavigationLabel);
      lockPageScroll(keepHeaderPinned);
      document.documentElement.classList.add("nav-is-open");
      document.body.classList.add("nav-is-open");
      setPageInert(true);

      window.setTimeout(() => {
        if (!isExpanded(mobileToggle) || mobileDrawer.hidden) return;
        const firstFocusable = visibleFocusableChildren(mobileDrawer)[0];
        firstFocusable?.focus({ preventScroll: true });
      }, 80);
    };

    megaTrigger?.addEventListener(
      "click",
      (event) => {
        event.stopPropagation();
        if (isExpanded(megaTrigger)) closeMegaMenu();
        else openMegaMenu();
      },
      { signal },
    );

    megaMenu?.addEventListener(
      "focusout",
      () => {
        window.setTimeout(() => {
          if (!isExpanded(megaTrigger)) return;
          if (megaMenu.contains(document.activeElement) || document.activeElement === megaTrigger) return;
          closeMegaMenu();
        }, 0);
      },
      { signal },
    );

    mobileToggle?.addEventListener(
      "click",
      () => {
        if (isExpanded(mobileToggle)) closeMobileDrawer(true);
        else openMobileDrawer();
      },
      { signal },
    );

    mobileBackdrop?.addEventListener("click", () => closeMobileDrawer(true), { signal });

    mobileDrawer?.querySelectorAll("a[href]").forEach((link) => {
      link.addEventListener("click", () => closeMobileDrawer(false), { signal });
    });

    document.addEventListener(
      "click",
      (event) => {
        if (
          isExpanded(megaTrigger) &&
          !megaMenu?.contains(event.target) &&
          !megaTrigger?.contains(event.target)
        ) {
          closeMegaMenu();
        }
      },
      { signal },
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Tab" && isExpanded(mobileToggle) && mobileDrawer) {
          const focusable = [mobileToggle, ...visibleFocusableChildren(mobileDrawer)].filter(Boolean);
          if (!focusable.length) return;
          const currentIndex = focusable.indexOf(document.activeElement);
          const direction = event.shiftKey ? -1 : 1;
          const fallbackIndex = event.shiftKey ? 0 : -1;
          const nextIndex = (currentIndex === -1 ? fallbackIndex : currentIndex) + direction;
          const wrappedIndex = (nextIndex + focusable.length) % focusable.length;
          event.preventDefault();
          focusable[wrappedIndex].focus({ preventScroll: true });
          return;
        }

        if (event.key !== "Escape") return;
        if (isExpanded(mobileToggle)) {
          closeMobileDrawer(true);
          return;
        }
        if (isExpanded(megaTrigger)) {
          closeMegaMenu(true);
        }
      },
      { signal },
    );

    const closeNavigationAcrossBreakpoint = (event) => {
      if (event.matches) closeMobileDrawer(false);
      else closeMegaMenu(false);
    };

    if (desktopNavigation.addEventListener) {
      desktopNavigation.addEventListener("change", closeNavigationAcrossBreakpoint, { signal });
    } else {
      desktopNavigation.addListener(closeNavigationAcrossBreakpoint);
    }

    window.addEventListener(
      "resize",
      () => {
        if (isExpanded(mobileToggle)) positionMobileDrawer();
      },
      { passive: true, signal },
    );

    const updateScrolledState = () => {
      scrollFrame = 0;
      header?.classList.toggle("is-scrolled", window.scrollY > 8);
    };

    const requestScrollUpdate = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateScrolledState);
    };

    updateScrolledState();
    window.addEventListener("scroll", requestScrollUpdate, { passive: true, signal });

    synchronizeCart();
    const cartObserver =
      cartLink && "MutationObserver" in window
        ? new MutationObserver(synchronizeCart)
        : null;
    cartObserver?.observe(cartLink, { childList: true, subtree: true });

    const drawerResizeObserver =
      "ResizeObserver" in window && root.querySelector(".glass-header-rail")
        ? new ResizeObserver(() => {
            if (isExpanded(mobileToggle)) positionMobileDrawer();
          })
        : null;
    if (drawerResizeObserver) drawerResizeObserver.observe(root.querySelector(".glass-header-rail"));

    root.ppShellCleanup = () => {
      if (isExpanded(mobileToggle)) closeMobileDrawer(false);
      controller.abort();
      cartObserver?.disconnect();
      drawerResizeObserver?.disconnect();
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      if (!desktopNavigation.removeEventListener) {
        desktopNavigation.removeListener(closeNavigationAcrossBreakpoint);
      }
      delete root.ppShellCleanup;
      delete root.dataset.ppShellReady;
    };
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.("[data-pp-site-header]")) initializeHeader(scope);
    scope.querySelectorAll?.("[data-pp-site-header]").forEach(initializeHeader);
  };

  let rebuyCart = null;
  let rebuyCartAttributeObserver = null;
  let rebuyCartWasInert = null;
  let rebuyCartReturnFocus = null;

  document.addEventListener("focusin", (event) => {
    if (rebuyCart?.getAttribute("aria-hidden") === "false") return;
    if (!(event.target instanceof HTMLElement) || rebuyCart?.contains(event.target)) return;
    rebuyCartReturnFocus = event.target;
  });

  const synchronizeRebuyCartAccessibility = () => {
    if (!rebuyCart) return;
    const shouldBeInert = rebuyCart.getAttribute("aria-hidden") !== "false";
    const activeElement = document.activeElement;
    const isClosing = rebuyCartWasInert === false && shouldBeInert;

    if (rebuyCart.inert !== shouldBeInert) {
      rebuyCart.inert = shouldBeInert;
    }

    if (
      isClosing &&
      rebuyCartReturnFocus &&
      document.contains(rebuyCartReturnFocus) &&
      (activeElement === document.body || activeElement === document.documentElement || rebuyCart.contains(activeElement))
    ) {
      rebuyCartReturnFocus.focus({ preventScroll: true });
    } else if (!shouldBeInert && !rebuyCart.contains(document.activeElement)) {
      rebuyCart.querySelector("#rebuy-cart-close")?.focus({ preventScroll: true });
    }

    rebuyCartWasInert = shouldBeInert;
  };

  const initializeRebuyCartAccessibility = () => {
    const nextRebuyCart = document.querySelector("#rebuy-cart");
    if (nextRebuyCart === rebuyCart) {
      synchronizeRebuyCartAccessibility();
      return;
    }

    rebuyCartAttributeObserver?.disconnect();
    rebuyCart = nextRebuyCart;
    rebuyCartWasInert = null;
    if (!rebuyCart) return;

    synchronizeRebuyCartAccessibility();
    rebuyCartAttributeObserver = new MutationObserver(synchronizeRebuyCartAccessibility);
    rebuyCartAttributeObserver.observe(rebuyCart, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
  };

  const rebuyCartDiscoveryObserver = new MutationObserver(initializeRebuyCartAccessibility);
  rebuyCartDiscoveryObserver.observe(document.documentElement, { childList: true, subtree: true });
  initializeRebuyCartAccessibility();
  document.addEventListener("rebuy.ready", initializeRebuyCartAccessibility);
  document.addEventListener("rebuy:cart.show", synchronizeRebuyCartAccessibility);
  document.addEventListener("rebuy:smartcart.show", synchronizeRebuyCartAccessibility);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const roots = event.target.matches?.("[data-pp-site-header]")
      ? [event.target]
      : all("[data-pp-site-header]", event.target);
    roots.forEach((root) => root.ppShellCleanup?.());
  });
})();
